import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { createAdvertisingBanner } from "../../../services/Admin/AdvertisingBannersApi";
// optional categories fetch - will fail silently if service not available
import { getAdminCategories } from "../../../services/Admin/ProductsAndCategoriesApi";

const AddAdvertisingBannerDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  // file states for uploads
  const [imageFile, setImageFile] = useState(null);
  const [imageWebFile, setImageWebFile] = useState(null);

  const [titleAr, setTitleAr] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [descAr, setDescAr] = useState("");
  const [descEn, setDescEn] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [displayOrder, setDisplayOrder] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [categoryIds, setCategoryIds] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      resetForm();
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await getAdminCategories();
      const data = res?.success ? res.data : res;
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      // ignore if service not present
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const resetForm = () => {
    setImageFile(null);
    setImageWebFile(null);
    setTitleAr("");
    setTitleEn("");
    setDescAr("");
    setDescEn("");
    setIsActive(true);
    setDisplayOrder("");
    setStartDate("");
    setEndDate("");
    setCategoryIds([]);
    setError("");
  };

  const handleSubmit = async () => {
    setError("");
    // minimal validation
    if (!titleAr.trim() && !titleEn.trim()) {
      setError("يرجى إدخال عنوان عربي أو إنجليزي");
      return;
    }
    const payload = {
      image: imageFile || null,
      image_web: imageWebFile || null,
      title_ar: titleAr.trim() || null,
      title_en: titleEn.trim() || null,
      description_ar: descAr.trim() || null,
      description_en: descEn.trim() || null,
      is_active: !!isActive,
      display_order: displayOrder ? Number(displayOrder) : null,
      start_date: startDate || null,
      end_date: endDate || null,
      category_ids: Array.isArray(categoryIds)
        ? categoryIds.map((id) => Number(id))
        : [],
    };

    setLoading(true);
    try {
      const res = await createAdvertisingBanner(payload);
      if (res?.message_en || res?.message_ar) {
        onSuccess?.();
      } else {
        setError(res?.message || "فشل إنشاء البانر");
      }
    } catch (err) {
      setError(err?.message || "حدث خطأ أثناء الإنشاء");
    } finally {
      setLoading(false);
    }
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>إضافة بانر إعلاني</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption">الصورة (mobile)</Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setImageFile(
                  e.target.files && e.target.files[0]
                    ? e.target.files[0]
                    : null,
                )
              }
              style={{ display: "block", marginTop: 8 }}
            />
            {imageFile && (
              <Typography variant="caption" color="text.secondary">
                {imageFile.name}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="caption">الصورة (web)</Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setImageWebFile(
                  e.target.files && e.target.files[0]
                    ? e.target.files[0]
                    : null,
                )
              }
              style={{ display: "block", marginTop: 8 }}
            />
            {imageWebFile && (
              <Typography variant="caption" color="text.secondary">
                {imageWebFile.name}
              </Typography>
            )}
          </Box>

          <TextField
            label="العنوان (عربي)"
            value={titleAr}
            onChange={(e) => setTitleAr(e.target.value)}
            fullWidth
          />
          <TextField
            label="العنوان (إنجليزي)"
            value={titleEn}
            onChange={(e) => setTitleEn(e.target.value)}
            fullWidth
          />
          <TextField
            label="الوصف (عربي)"
            value={descAr}
            onChange={(e) => setDescAr(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="الوصف (إنجليزي)"
            value={descEn}
            onChange={(e) => setDescEn(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth size="small">
            <InputLabel>التصنيفات</InputLabel>
            <Select
              multiple
              value={categoryIds}
              onChange={(e) =>
                setCategoryIds(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value,
                )
              }
              input={<OutlinedInput label="التصنيفات" />}
              renderValue={(selected) =>
                (selected || [])
                  .map((id) => {
                    const c = categories.find(
                      (x) => Number(x.id) === Number(id),
                    );
                    return c ? c.name_ar || c.name_en : id;
                  })
                  .join(", ")
              }
              MenuProps={MenuProps}
            >
              {categoriesLoading ? (
                <MenuItem disabled>جارٍ التحميل...</MenuItem>
              ) : (
                categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Checkbox checked={categoryIds.indexOf(c.id) > -1} />
                    <ListItemText primary={c.name_ar || c.name_en} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={isActive ? "مفعّل" : "معطّل"}
            />
            <TextField
              label="الترتيب"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              size="small"
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="تاريخ البدء"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="تاريخ الانتهاء"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={loading}>
          إلغاء
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={18} /> : "حفظ"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAdvertisingBannerDialog;
